import { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { ChevronDown, Mail, Menu, Phone, X } from "lucide-react";
import { PRACTICES } from "../data/marketing";

const navLinks = [
  { label: "Technology", href: "/technology", id: "technology" },
  { label: "Insights", href: "/insights", id: "insights" },
  { label: "About", href: "/about", id: "about" },
  { label: "Contact", href: "/contact", id: "contact" },
];

const linkCls = (active, dark = false) => {
  if (dark) {
    return `rounded-full px-4 py-2 text-sm font-semibold transition-colors ${active ? "bg-white text-forest-deep" : "text-emerald-100/70 hover:bg-white/10 hover:text-white"}`;
  }
  return `rounded-full px-4 py-2 text-sm font-semibold transition-colors ${active ? "bg-forest-deep text-white" : "text-forest-deep/70 hover:bg-forest-deep/5 hover:text-forest-deep"}`;
};

export default function HeaderNav() {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const { pathname } = useLocation();
  const practicesActive = pathname === "/practices" || pathname.startsWith("/practice/");

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 16);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => setOpen(false), [pathname]);

  return (
    <div className="fixed inset-x-0 top-0 z-50">
      <a href="#main" className="sr-only focus:not-sr-only focus:absolute focus:left-4 focus:top-4 focus:z-[70] focus:rounded-full focus:bg-white focus:px-4 focus:py-2 focus:text-sm focus:font-bold focus:text-forest-deep">
        Skip to content
      </a>
      <div className="bg-forest-deep text-emerald-100">
        <div className="mx-auto flex max-w-7xl items-center justify-between gap-4 px-4 py-2 text-xs sm:px-6">
          <a href="tel:+918240968144" className="inline-flex items-center gap-2 font-semibold hover:text-emerald-300" data-testid="topbar-phone-link">
            <Phone className="h-3.5 w-3.5 text-emerald-300" /> +91 8240968144 <span className="hidden text-emerald-100/55 sm:inline">· Advisory desk</span>
          </a>
          <span className="hidden text-emerald-100/60 md:inline">Specialist consulting. Proprietary client technology.</span>
          <a href="mailto:alik@clymaco.com" className="hidden items-center gap-2 font-medium hover:text-emerald-300 sm:inline-flex" data-testid="topbar-email-link">
            <Mail className="h-3.5 w-3.5 text-emerald-300" /> alik@clymaco.com
          </a>
        </div>
      </div>

      <header className={`border-b backdrop-blur-xl transition-all duration-300 ${scrolled ? "border-white/10 bg-forest-deep/95 shadow-2xl shadow-forest-deep/25" : "border-forest-deep/10 bg-[#FBF9F4]"}`}>
        <div className="mx-auto flex max-w-7xl items-center justify-between gap-4 px-4 py-3 sm:px-6">
          <Link to="/" className="flex items-center gap-3" data-testid="nav-brand-logo" aria-label="CLYMACO home">
            <span className={`rounded-2xl border px-3 py-2 shadow-sm transition-all duration-300 ${scrolled ? "border-white/70 bg-white shadow-black/20" : "border-forest-deep/10 bg-white"}`}>
              <img src="/logo-clymaco.webp" alt="CLYMACO Sustainability Services" className="h-8 w-auto" />
            </span>
            <span className={`hidden rounded-full border px-3 py-1 font-mono text-[9px] font-semibold uppercase tracking-[0.2em] sm:block ${scrolled ? "border-white/20 text-emerald-100/65" : "border-forest-deep/10 text-forest-deep/55"}`}>Consulting + SaaS</span>
          </Link>

          <nav className="hidden items-center gap-1 lg:flex" aria-label="Primary navigation">
            <Link to="/" className={linkCls(pathname === "/", scrolled)} data-testid="nav-link-home">Home</Link>
            <div className="group relative">
              <Link to="/practices" className={`${linkCls(practicesActive, scrolled)} inline-flex items-center gap-1.5`} data-testid="nav-link-practices">
                Practices <ChevronDown className="h-3.5 w-3.5 transition-transform group-hover:rotate-180" />
              </Link>
              <div className="invisible absolute left-0 top-full w-80 translate-y-2 rounded-3xl border border-forest-deep/10 bg-white p-2 opacity-0 shadow-2xl shadow-forest-deep/15 transition-all group-hover:visible group-hover:translate-y-1 group-hover:opacity-100 group-focus-within:visible group-focus-within:translate-y-1 group-focus-within:opacity-100" data-testid="practices-dropdown">
                <Link to="/practices" className="block rounded-2xl px-4 py-3 text-sm font-bold text-forest-deep hover:bg-forest-deep/5" data-testid="dropdown-practices-overview">Practices overview</Link>
                {PRACTICES.map((practice) => (
                  <Link key={practice.slug} to={`/practice/${practice.slug}`} className="block rounded-2xl px-4 py-3 hover:bg-forest-deep/5" data-testid={`dropdown-practice-${practice.slug}`}>
                    <span className="block text-sm font-bold text-forest-deep">{practice.title}</span>
                    <span className="mt-0.5 block text-xs text-forest-deep/55">{practice.audience}</span>
                  </Link>
                ))}
              </div>
            </div>
            {navLinks.map((link) => (
              <Link key={link.id} to={link.href} className={linkCls(pathname === link.href, scrolled)} data-testid={`nav-link-${link.id}`}>
                {link.label}
              </Link>
            ))}
          </nav>

          <div className="hidden items-center gap-2 lg:flex">
            <Link to="/technology#walkthrough" className={`rounded-full border px-4 py-2 text-sm font-bold transition-colors ${scrolled ? "border-white/20 text-white hover:bg-white/10" : "border-forest-deep/15 text-forest-deep hover:bg-forest-deep/5"}`} data-testid="nav-cta-walkthrough">
              Arrange a walkthrough
            </Link>
            <Link to="/contact" className={`rounded-full px-5 py-2.5 text-sm font-bold transition-colors ${scrolled ? "bg-emerald-400 text-forest-deep hover:bg-emerald-300" : "bg-forest-primary text-white hover:bg-forest-rich"}`} data-testid="nav-cta-consultation">
              Discuss a mandate
            </Link>
          </div>

          <button type="button" onClick={() => setOpen((v) => !v)} className={`rounded-xl border p-2 transition-colors lg:hidden ${scrolled ? "border-white/20 text-white" : "border-forest-deep/10 text-forest-deep"}`} aria-expanded={open} aria-label="Toggle menu" data-testid="mobile-menu-button">
            {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </button>
        </div>

        <AnimatePresence>
          {open && (
            <motion.nav initial={{ height: 0, opacity: 0 }} animate={{ height: "auto", opacity: 1 }} exit={{ height: 0, opacity: 0 }} className="overflow-hidden border-t border-forest-deep/10 bg-[#FBF9F4] lg:hidden" aria-label="Mobile navigation">
              <div className="max-h-[75vh] space-y-1 overflow-y-auto px-4 py-4">
                <Link to="/" className="block rounded-xl px-4 py-3 text-sm font-bold text-forest-deep hover:bg-forest-deep/5" data-testid="mobile-nav-link-home">Home</Link>
                <Link to="/practices" className="block rounded-xl px-4 py-3 text-sm font-bold text-forest-deep hover:bg-forest-deep/5" data-testid="mobile-nav-link-practices">Practices</Link>
                <div className="ml-4 border-l border-forest-deep/10 pl-3">
                  {PRACTICES.map((practice) => (
                    <Link key={practice.slug} to={`/practice/${practice.slug}`} className="block rounded-xl px-3 py-2 text-sm font-semibold text-forest-deep/65 hover:bg-forest-deep/5" data-testid={`mobile-practice-link-${practice.slug}`}>
                      {practice.title}
                    </Link>
                  ))}
                </div>
                {navLinks.map((link) => (
                  <Link key={link.id} to={link.href} className="block rounded-xl px-4 py-3 text-sm font-bold text-forest-deep hover:bg-forest-deep/5" data-testid={`mobile-nav-link-${link.id}`}>
                    {link.label}
                  </Link>
                ))}
                <Link to="/technology#walkthrough" className="block rounded-xl border border-forest-deep/15 px-4 py-3 text-sm font-bold text-forest-deep" data-testid="mobile-nav-cta-walkthrough">
                  Arrange a walkthrough
                </Link>
              </div>
            </motion.nav>
          )}
        </AnimatePresence>
      </header>
    </div>
  );
}
